import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "rhtv_favorites";

function readFavorites(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return [];
  }
}

export function useFavorites() {
  const [favorites, setFavorites] = useState<string[]>(() => readFavorites());

  useEffect(() => {
    const onChange = () => setFavorites(readFavorites());
    window.addEventListener("rhtv-favorites-change", onChange);
    window.addEventListener("storage", onChange);
    return () => {
      window.removeEventListener("rhtv-favorites-change", onChange);
      window.removeEventListener("storage", onChange);
    };
  }, []);

  const persist = useCallback((next: string[]) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      /* ignore storage errors */
    }
    setFavorites(next);
    window.dispatchEvent(new Event("rhtv-favorites-change"));
  }, []);

  const isFavorite = useCallback((id: string) => favorites.includes(id), [favorites]);

  const toggleFavorite = useCallback(
    (id: string) => {
      const current = readFavorites();
      const next = current.includes(id)
        ? current.filter((x) => x !== id)
        : [...current, id];
      persist(next);
      return next.includes(id);
    },
    [persist],
  );

  const removeFavorite = useCallback(
    (id: string) => {
      const next = readFavorites().filter((x) => x !== id);
      persist(next);
    },
    [persist],
  );

  const clearFavorites = useCallback(() => persist([]), [persist]);

  return { favorites, isFavorite, toggleFavorite, removeFavorite, clearFavorites };
}